import React, {
  useEffect,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";

import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

import { db } from "../firebase";

import { ALLOWED_AREAS } from "../constants/deliveryAreas";

export default function DeliveryDashboard() {

  const navigate = useNavigate();

  const {
    user,
    loading: authLoading,
  } = useAuth();


  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [deliveryPerson, setDeliveryPerson] =
    useState(null);

  const [deliveryRequests, setDeliveryRequests] =
    useState([]);

  const [now, setNow] =
    useState(Date.now());

const [deliveryAreas, setDeliveryAreas] =
  useState([]);

  const [newDeliveryArea, setNewDeliveryArea] =
  useState("");

const [newDeliveryCharge, setNewDeliveryCharge] =
  useState("");

  const [deliveryCharge, setDeliveryCharge] =
  useState("");

  const [areaDropdownOpen, setAreaDropdownOpen] =
  useState(false);

const [areaSearch, setAreaSearch] =
  useState("");

  /* =========================================================
     LOAD DELIVERY PERSON
  ========================================================= */

  useEffect(() => {

    if (authLoading) return;

    if (!user) {

      sessionStorage.setItem(
        "redirectAfterLogin",
        "/delivery-dashboard"
      );

      navigate("/login");

      return;
    }

    loadDeliveryPerson();

  }, [
    user,
    authLoading,
    navigate,
  ]);


  /* =========================================================
     LOAD DELIVERY PERSON
  ========================================================= */

  const loadDeliveryPerson =
    async () => {

      if (!user) return;

      try {

        setLoading(true);

        const ref =
          doc(
            db,
            "deliveryPersons",
            user.uid
          );

        const snap =
          await getDoc(ref);


        if (!snap.exists()) {

          alert(
            "You have not applied as a delivery person yet."
          );

          navigate("/join-delivery");

          return;
        }


        const data = {
          id: snap.id,
          ...snap.data(),
        };


        setDeliveryPerson(data);

        setDeliveryAreas(
  Array.isArray(data.deliveryAreas)
    ? data.deliveryAreas
    : data.area
      ? [
          {
            area: data.area,
            charge: Number(
              data.deliveryCharge || 0
            ),
          },
        ]
      : []
);


        const approved =
          data.applicationStatus ===
            "APPROVED" ||
          data.verified === true;


        if (!approved) {
          return;
        }


        /*
         * Expired availability
         */

        if (
          data.available === true &&
          data.availabilityExpiresAt
        ) {

          const expiry =
            data.availabilityExpiresAt.toMillis
              ? data.availabilityExpiresAt.toMillis()
              : new Date(
                  data.availabilityExpiresAt
                ).getTime();


          if (
            Date.now() >= expiry &&
            !data.currentOrderId
          ) {

            await updateDoc(
              ref,
              {
                available: false,

                availabilityExpiresAt:
                  null,

                updatedAt:
                  serverTimestamp(),
              }
            );

          }

        }

      } catch (error) {

        console.error(
          "Failed to load delivery person:",
          error
        );

        alert(
          "Unable to load delivery dashboard."
        );

      } finally {

        setLoading(false);

      }

    };


  /* =========================================================
     LIVE DELIVERY REQUEST LISTENER
  ========================================================= */

  useEffect(() => {

    if (
      !user ||
      !deliveryPerson
    ) {
      return;
    }


    const q =
      query(
        collection(
          db,
          "deliveryRequests"
        ),

        where(
          "deliveryPersonUid",
          "==",
          user.uid
        )
      );


    const unsubscribe =
      onSnapshot(

        q,

        (snapshot) => {

          const requests =
            snapshot.docs
              .map((requestDoc) => ({
                id: requestDoc.id,
                ...requestDoc.data(),
              }))
              .filter(
                (request) =>
                  request.status ===
                    "ASSIGNED" ||
                  request.status ===
                    "ACCEPTED"
              );


          /*
           * Latest request first
           */

          requests.sort(
            (a, b) => {

              const ta =
                a.createdAt?.seconds ||
                0;

              const tb =
                b.createdAt?.seconds ||
                0;

              return tb - ta;
            }
          );


          setDeliveryRequests(
            requests
          );

        },

        (error) => {

          console.error(
            "Delivery requests listener error:",
            error
          );

          setDeliveryRequests([]);

        }

      );


    return () =>
      unsubscribe();

  }, [
    user,
    deliveryPerson,
  ]);


  /* =========================================================
     CLOCK
  ========================================================= */

  useEffect(() => {

    const timer =
      setInterval(
        () => {
          setNow(Date.now());
        },
        1000
      );


    return () =>
      clearInterval(timer);

  }, []);


  /* =========================================================
     AVAILABILITY EXPIRY
  ========================================================= */

  useEffect(() => {

    if (
      !deliveryPerson?.available
    ) {
      return;
    }


    if (
      !deliveryPerson?.availabilityExpiresAt
    ) {
      return;
    }


    /*
     * Do NOT expire availability
     * while an order is active.
     */

    if (
      deliveryPerson.currentOrderId
    ) {
      return;
    }


    const expiry =
      deliveryPerson
        .availabilityExpiresAt
        .toMillis
        ? deliveryPerson
            .availabilityExpiresAt
            .toMillis()
        : new Date(
            deliveryPerson
              .availabilityExpiresAt
          ).getTime();


    if (
      now >= expiry
    ) {

      expireAvailability();

    }

  }, [
    now,
    deliveryPerson?.available,
    deliveryPerson?.availabilityExpiresAt,
    deliveryPerson?.currentOrderId,
  ]);


  /* =========================================================
     EXPIRE AVAILABILITY
  ========================================================= */

  const expireAvailability =
    async () => {

      if (!user) return;


      try {

        const ref =
          doc(
            db,
            "deliveryPersons",
            user.uid
          );


        await updateDoc(
          ref,
          {
            available: false,

            availabilityExpiresAt:
              null,

            updatedAt:
              serverTimestamp(),
          }
        );


        setDeliveryPerson(
          (prev) => ({
            ...prev,

            available: false,

            availabilityExpiresAt:
              null,
          })
        );

      } catch (error) {

        console.error(
          "Availability expiry error:",
          error
        );

      }

    };

/* =========================================================
   SAVE DELIVERY CHARGE
========================================================= */

const saveDeliveryCharge = async () => {
  if (!user || saving) return;

  const charge = Number(deliveryCharge);

  if (!Number.isFinite(charge) || charge < 0) {
    alert("Please enter a valid delivery charge.");
    return;
  }

  if (charge > 10000) {
    alert("Delivery charge cannot exceed ₹10,000.");
    return;
  }

  try {
    setSaving(true);

    const ref = doc(
      db,
      "deliveryPersons",
      user.uid
    );

    await updateDoc(ref, {
      deliveryCharge: charge,
      updatedAt: serverTimestamp(),
    });

    setDeliveryPerson((prev) => ({
      ...prev,
      deliveryCharge: charge,
    }));

    setDeliveryCharge(String(charge));

    alert("✅ Delivery charge updated.");
  } catch (error) {
    console.error(
      "Delivery charge update error:",
      error
    );

    alert(
      "Unable to update delivery charge."
    );
  } finally {
    setSaving(false);
  }
};

const addDeliveryArea = async (
  area,
  charge
) => {
  if (!user || saving) return;

  const cleanArea = String(area || "").trim();
  const numericCharge = Number(charge);

  if (!cleanArea) {
    alert("Please select a delivery area.");
    return;
  }

  if (
    !Number.isFinite(numericCharge) ||
    numericCharge < 0
  ) {
    alert("Please enter a valid delivery charge.");
    return;
  }

  if (numericCharge > 10000) {
    alert(
      "Delivery charge cannot exceed ₹10,000."
    );
    return;
  }

  const alreadyExists =
    deliveryAreas.some(
      (item) =>
        item.area.trim().toLowerCase() ===
        cleanArea.toLowerCase()
    );

  if (alreadyExists) {
    alert(
      "You already deliver to this area."
    );
    return;
  }

  try {
    setSaving(true);

    const updatedAreas = [
      ...deliveryAreas,
      {
        area: cleanArea,
        charge: numericCharge,
      },
    ];

    const ref = doc(
      db,
      "deliveryPersons",
      user.uid
    );

    await updateDoc(ref, {
      deliveryAreas: updatedAreas,

      // Keep old fields for compatibility
      area:
        deliveryAreas.length === 0
          ? cleanArea
          : deliveryPerson?.area || cleanArea,

      deliveryCharge:
        deliveryAreas.length === 0
          ? numericCharge
          : deliveryPerson?.deliveryCharge || numericCharge,

      updatedAt:
        serverTimestamp(),
    });

    setDeliveryAreas(updatedAreas);

    setDeliveryPerson((prev) => ({
      ...prev,
      deliveryAreas: updatedAreas,
    }));

  } catch (error) {
    console.error(
      "Add delivery area error:",
      error
    );

    alert(
      "Unable to add delivery area."
    );
  } finally {
    setSaving(false);
  }
};

const removeDeliveryArea = async (
  index
) => {
  if (!user || saving) return;

  if (deliveryAreas.length <= 1) {
    alert(
      "You must keep at least one delivery area."
    );
    return;
  }

  const areaToRemove =
    deliveryAreas[index];

  if (
    !window.confirm(
      `Remove "${areaToRemove.area}" from your delivery areas?`
    )
  ) {
    return;
  }

  try {
    setSaving(true);

    const updatedAreas =
      deliveryAreas.filter(
        (_, i) => i !== index
      );

    const ref = doc(
      db,
      "deliveryPersons",
      user.uid
    );

    await updateDoc(ref, {
      deliveryAreas: updatedAreas,

      // Keep legacy fields pointing
      // to the first remaining area.
      area: updatedAreas[0].area,

      deliveryCharge:
        updatedAreas[0].charge,

      updatedAt:
        serverTimestamp(),
    });

    setDeliveryAreas(updatedAreas);

    setDeliveryPerson((prev) => ({
      ...prev,
      deliveryAreas: updatedAreas,
      area: updatedAreas[0].area,
      deliveryCharge:
        updatedAreas[0].charge,
    }));

  } catch (error) {
    console.error(
      "Remove delivery area error:",
      error
    );

    alert(
      "Unable to remove delivery area."
    );
  } finally {
    setSaving(false);
  }
};


/* =========================================================
   CHECK IN
========================================================= */

const checkIn = async () => {

  if (
    !user ||
    saving
  ) {
    return;
  }

  /*
   * Do not allow another availability
   * session while delivery is active.
   */

  if (
    deliveryPerson?.currentOrderId
  ) {

    alert(
      "Complete your current delivery first."
    );

    return;
  }

  try {

    setSaving(true);

    /*
     * =====================================================
     NEW 24-HOUR AVAILABILITY SESSION
     * =====================================================
     */

    const nowDate = new Date();

    const expiryDate =
  new Date(
    nowDate.getTime() +
    24 * 60 * 60 * 1000
  );

    const ref =
      doc(
        db,
        "deliveryPersons",
        user.uid
      );

    /*
     * =====================================================
     * SAVE AVAILABILITY
     *
     * reminderSent: false
     *
     * This is IMPORTANT.
     *
     * Every time the delivery person checks in
     * for a new hour, the 10-minute reminder
     * must become eligible again.
     * =====================================================
     */

    await updateDoc(
      ref,
      {

        available:
          true,

        lastCheckIn:
          serverTimestamp(),

        availabilityExpiresAt:
          Timestamp.fromDate(
            expiryDate
          ),

        /*
         * Reset the expiry reminder
         * for this new availability session.
         */

        availabilityExpiryReminderSent:
          false,

        availabilityExpiryReminderSentAt:
          null,

        updatedAt:
          serverTimestamp(),

      }
    );

    /*
     * =====================================================
     * UPDATE LOCAL STATE
     * =====================================================
     */

    setDeliveryPerson(
      (prev) => ({
        ...prev,

        available:
          true,

        lastCheckIn:
          new Date(),

        availabilityExpiresAt:
          Timestamp.fromDate(
            expiryDate
          ),

        availabilityExpiryReminderSent:
          false,

        availabilityExpiryReminderSentAt:
          null,

      })
    );

  } catch (error) {

    console.error(
      "Check-in error:",
      error
    );

    alert(
      "Unable to check in. Please try again."
    );

  } finally {

    setSaving(false);

  }

};

  /* =========================================================
   CHECK OUT
========================================================= */

const checkOut = async () => {

  if (
    !user ||
    saving
  ) {
    return;
  }

  if (
    deliveryPerson?.currentOrderId
  ) {

    alert(
      "You have an active delivery. Complete it before becoming unavailable."
    );

    return;
  }

  try {

    setSaving(true);

    const ref =
      doc(
        db,
        "deliveryPersons",
        user.uid
      );

    await updateDoc(
      ref,
      {

        available:
          false,

        availabilityExpiresAt:
          null,

        availabilityExpiryReminderSent:
          false,

        availabilityExpiryReminderSentAt:
          null,

        updatedAt:
          serverTimestamp(),

      }
    );

    setDeliveryPerson(
      (prev) => ({
        ...prev,

        available:
          false,

        availabilityExpiresAt:
          null,

        availabilityExpiryReminderSent:
          false,

        availabilityExpiryReminderSentAt:
          null,

      })
    );

  } catch (error) {

    console.error(
      "Check-out error:",
      error
    );

    alert(
      "Unable to change availability."
    );

  } finally {

    setSaving(false);

  }

};

  /* =========================================================
     ACCEPT DELIVERY
  ========================================================= */

  const acceptDelivery =
    async (request) => {

      if (
        !user ||
        saving ||
        !request?.id
      ) {
        return;
      }


      if (
        deliveryPerson?.currentOrderId
      ) {

        alert(
          "You already have an active delivery."
        );

        return;
      }


      if (
        !window.confirm(
          `Accept delivery ${request.orderId}?`
        )
      ) {
        return;
      }


      try {

        setSaving(true);


        const requestRef =
          doc(
            db,
            "deliveryRequests",
            request.id
          );


        const personRef =
          doc(
            db,
            "deliveryPersons",
            user.uid
          );


        /*
         * Transaction prevents two devices
         * accepting the same request.
         */

        await runTransaction(
          db,
          async (transaction) => {

            const requestSnap =
              await transaction.get(
                requestRef
              );


            const personSnap =
              await transaction.get(
                personRef
              );


            if (
              !requestSnap.exists()
            ) {

              throw new Error(
                "Delivery request no longer exists."
              );

            }


            if (
              !personSnap.exists()
            ) {

              throw new Error(
                "Delivery person profile not found."
              );

            }


            const requestData =
              requestSnap.data();

            const personData =
              personSnap.data();


            /*
             * Request must still be available.
             */

            if (
              requestData.status !==
              "ASSIGNED"
            ) {

              throw new Error(
                "This delivery has already been accepted or rejected."
              );

            }


            /*
             * Make sure request belongs
             * to this delivery person.
             */

            if (
              requestData.deliveryPersonUid !==
              user.uid
            ) {

              throw new Error(
                "This delivery request is not assigned to you."
              );

            }


            /*
             * Make sure person doesn't
             * already have another order.
             */

            if (
              personData.currentOrderId
            ) {

              throw new Error(
                "You already have an active delivery."
              );

            }


            /*
             * ACCEPT REQUEST
             */

            transaction.update(
              requestRef,
              {

                status:
                  "ACCEPTED",

                deliveryStatus:
                  "ACCEPTED",

                acceptedAt:
                  serverTimestamp(),

                updatedAt:
                  serverTimestamp(),

              }
            );


            /*
             * UPDATE DELIVERY PERSON
             */

            transaction.update(
  personRef,
  {

    currentOrderId:
      requestData.orderId,

    currentDeliveryRequestId:
      request.id,

    // Do not accept new deliveries
    // while this order is active.
    available:
      false,

    // IMPORTANT:
    // Keep the existing availability expiry.
    // The 24-hour session continues running.
    availabilityExpiresAt:
      personData.availabilityExpiresAt || null,

    updatedAt:
      serverTimestamp(),

  }
);

          }
        );


        /*
         * Local UI update
         */

        setDeliveryPerson(
  (prev) => ({
    ...prev,

    currentOrderId:
      request.orderId,

    currentDeliveryRequestId:
      request.id,

    available:
      false,

    // IMPORTANT:
    // Keep the original timer.
    availabilityExpiresAt:
      prev.availabilityExpiresAt,
  })
);


        alert(
          "✅ Delivery accepted."
        );


      } catch (error) {

        console.error(
          "Accept delivery error:",
          error
        );

        alert(
          error.message ||
          "Unable to accept delivery."
        );

      } finally {

        setSaving(false);

      }

    };

    /* =========================================================
   CALL CUSTOMER
========================================================= */

const callCustomer = (request) => {

  if (!request?.customerMobile) {
    alert("Customer phone number is not available.");
    return;
  }

  let phone = String(
    request.customerMobile
  ).trim();

  // Remove spaces, brackets, hyphens etc.
  phone = phone.replace(/[^\d+]/g, "");

  // Add India country code if it is a 10-digit number
  if (
    phone.length === 10 &&
    !phone.startsWith("+")
  ) {
    phone = `+91${phone}`;
  }

  window.location.href = `tel:${phone}`;
};

/* =========================================================
   MARK DELIVERY AS DELIVERED
========================================================= */

const markDelivered = async (request) => {

  if (
    !request ||
    !request.id ||
    !user
  ) {
    return;
  }

  if (
    !window.confirm(
      `Mark order ${request.orderId} as delivered?`
    )
  ) {
    return;
  }

  try {

    setSaving(true);

    /*
     * =================================================
     * REFERENCES
     * =================================================
     */

    const requestRef =
      doc(
        db,
        "deliveryRequests",
        request.id
      );

    /*
     * IMPORTANT:
     *
     * The delivery request ID is now
     * the shopOrders document ID.
     *
     * Fallback to request.id also keeps
     * older requests working.
     */

    const shopOrderDocId =
      request.shopOrderDocId ||
      request.id;

    const shopOrderRef =
      doc(
        db,
        "shopOrders",
        shopOrderDocId
      );

    const personRef =
      doc(
        db,
        "deliveryPersons",
        user.uid
      );

    /*
     * =================================================
     * TRANSACTION
     * =================================================
     */

    await runTransaction(
      db,
      async (transaction) => {

        /*
         * =================================================
         * READ DELIVERY REQUEST
         * =================================================
         */

        const requestSnap =
          await transaction.get(
            requestRef
          );

        /*
         * =================================================
         * READ SHOP ORDER
         * =================================================
         */

        const shopOrderSnap =
          await transaction.get(
            shopOrderRef
          );

        /*
         * =================================================
         * READ DELIVERY PERSON
         * =================================================
         */

        const personSnap =
          await transaction.get(
            personRef
          );

        /*
         * =================================================
         * VALIDATION
         * =================================================
         */

        if (
          !requestSnap.exists()
        ) {
          throw new Error(
            "Delivery request no longer exists."
          );
        }

        if (
          !shopOrderSnap.exists()
        ) {
          throw new Error(
            "Shop order could not be found."
          );
        }

        if (
          !personSnap.exists()
        ) {
          throw new Error(
            "Delivery person profile not found."
          );
        }

        const requestData =
          requestSnap.data();

        const shopOrderData =
          shopOrderSnap.data();

        const personData =
          personSnap.data();

        /*
         * =================================================
         * SECURITY CHECK
         * =================================================
         *
         * Make sure this delivery was actually
         * assigned to the logged-in delivery person.
         */

        if (
          requestData.deliveryPersonUid !==
          user.uid
        ) {
          throw new Error(
            "This delivery is not assigned to you."
          );
        }

        /*
         * =================================================
         * ONLY ACCEPTED DELIVERY
         * CAN BE COMPLETED
         * =================================================
         */

        if (
          requestData.status !==
          "ACCEPTED"
        ) {

          if (
            requestData.status ===
            "DELIVERED"
          ) {
            throw new Error(
              "This delivery is already completed."
            );
          }

          throw new Error(
            "This delivery has not been accepted."
          );
        }

        /*
         * =================================================
         * MAKE SURE THIS IS
         * DELIVERY PERSON'S CURRENT ORDER
         * =================================================
         */

        if (
          personData.currentOrderId !==
          (
            shopOrderData.orderId ||
            requestData.orderId
          )
        ) {
          throw new Error(
            "This is not your current delivery."
          );
        }

        /*
         * =================================================
         * 1. DELIVERY REQUEST → DELIVERED
         * =================================================
         */

        transaction.update(
          requestRef,
          {

            status:
              "DELIVERED",

            deliveryStatus:
              "DELIVERED",

            deliveredAt:
              serverTimestamp(),

            deliveredByUid:
              user.uid,

            updatedAt:
              serverTimestamp(),

          }
        );

        /*
         * =================================================
         * 2. SHOP ORDER
         *
         * DELIVERY PERSON HAS DELIVERED THE PACKAGE.
         *
         * IMPORTANT:
         * Do NOT make the final order status
         * "Delivered" yet.
         *
         * Customer must confirm receipt first.
         * =================================================
         */

        transaction.update(
  shopOrderRef,
  {
    status:
      "Ready for delivery",

    deliveryStatus:
      "DELIVERED",

    deliveredAt:
      serverTimestamp(),

    deliveredByUid:
      user.uid,

    deliveryRequestId:
      request.id,

    customerReceived:
      false,

    customerReceivedAt:
      null,

    updatedAt:
      serverTimestamp(),
  }
);

        /*
         * =================================================
         * 3. CUSTOMER NOTIFICATION
         *
         * Tell the customer that the delivery person
         * has delivered the order and ask them to
         * confirm receipt.
         * =================================================
         */

        const customerUid =
          requestData.customerUid ||
          shopOrderData.customerUid;

        if (
          customerUid
        ) {

          /*
           * Use a predictable notification ID.
           *
           * This prevents duplicate notifications
           * if the transaction is retried.
           */

          const activityRef =
            doc(
              db,
              "activity",
              `${request.id}_delivery_confirmation`
            );

          transaction.set(
            activityRef,
            {

              uid:
                customerUid,

              type:
                "delivery_confirmation",

              title:
                "📦 Order Delivered",

              message:
                `Your order #${
                  requestData.orderId ||
                  shopOrderData.orderId ||
                  ""
                } has been delivered. Please confirm that you received it.`,

              orderId:
                requestData.orderId ||
                shopOrderData.orderId ||
                "",

              shopOrderDocId:
                shopOrderDocId,

              deliveryRequestId:
                request.id,

              deliveryPersonUid:
                user.uid,

              /*
               * Keep notification unread so it
               * appears in the customer's For You
               * notification area.
               */

              read:
                false,

              /*
               * Customer can tap the notification
               * and go to My Orders.
               */

              action:
                "/orders",

              priority:
                "high",

              createdAt:
                serverTimestamp(),

              updatedAt:
                serverTimestamp(),

            },
            {
              merge: true,
            }
          );

        }

        /*
         * =================================================
         * 4. CLEAR DELIVERY PERSON CURRENT ORDER
         * =================================================
         *
         * The delivery person is no longer handling
         * this order.
         *
         * available = true allows them to receive
         * another order if their availability timer
         * is still active.
         *
         * IMPORTANT:
         * We do NOT touch availabilityExpiresAt.
         // The original 24-hour availability session
// continues.
         */

        transaction.update(
          personRef,
          {

            currentOrderId:
              null,

            currentDeliveryStatus:
              "DELIVERED",

            available:
              true,

            updatedAt:
              serverTimestamp(),

          }
        );

      }
    );

    /*
     * =================================================
     * UPDATE LOCAL DELIVERY PERSON STATE
     * =================================================
     */

    setDeliveryPerson(
      (prev) => ({
        ...prev,

        currentOrderId:
          null,

        currentDeliveryStatus:
          "DELIVERED",

        available:
          true,

        /*
         * IMPORTANT:
         * Do NOT clear availabilityExpiresAt here.
         *
         * The delivery person's existing
         * availability timer should continue.
         */

        availabilityExpiresAt:
          prev.availabilityExpiresAt,
      })
    );

    /*
     * =================================================
     * REMOVE COMPLETED REQUEST FROM LOCAL LIST
     * =================================================
     */

    setDeliveryRequests(
      (prev) =>
        prev.filter(
          (item) =>
            item.id !== request.id
        )
    );

    /*
     * =================================================
     * SUCCESS MESSAGE
     * =================================================
     */

    alert(
      "Delivery marked as delivered. Customer has been notified to confirm receipt."
    );

  } catch (error) {

    console.error(
      "Mark delivered error:",
      error
    );

    alert(
      error.message ||
      "Unable to mark delivery as delivered."
    );

  } finally {

    setSaving(false);

  }
};

  /* =========================================================
     REJECT DELIVERY
  ========================================================= */

  const rejectDelivery =
    async (request) => {

      if (
        !user ||
        saving ||
        !request?.id
      ) {
        return;
      }


      if (
        !window.confirm(
          `Reject delivery ${request.orderId}?`
        )
      ) {
        return;
      }


      try {

        setSaving(true);


        const requestRef =
          doc(
            db,
            "deliveryRequests",
            request.id
          );


        /*
         * Transaction makes sure we don't
         * reject an already accepted request.
         */

        await runTransaction(
          db,
          async (transaction) => {

            const snap =
              await transaction.get(
                requestRef
              );


            if (
              !snap.exists()
            ) {

              throw new Error(
                "Delivery request no longer exists."
              );

            }


            const data =
              snap.data();


            if (
              data.deliveryPersonUid !==
              user.uid
            ) {

              throw new Error(
                "This request is not assigned to you."
              );

            }


            if (
              data.status !==
              "ASSIGNED"
            ) {

              throw new Error(
                "This request is no longer available."
              );

            }


            transaction.update(
              requestRef,
              {

                status:
                  "REJECTED",

                deliveryStatus:
                  "REJECTED",

                rejectedAt:
                  serverTimestamp(),

                updatedAt:
                  serverTimestamp(),

              }
            );

          }
        );


        /*
         * Remove from local list immediately.
         */

        setDeliveryRequests(
          (prev) =>
            prev.filter(
              (item) =>
                item.id !== request.id
            )
        );


        alert(
          "Delivery request rejected."
        );


      } catch (error) {

        console.error(
          "Reject delivery error:",
          error
        );

        alert(
          error.message ||
          "Unable to reject delivery."
        );

      } finally {

        setSaving(false);

      }

    };


  /* =========================================================
     FORMAT TIME
  ========================================================= */

  const formatTime =
    (date) => {

      if (!date) {
        return "--";
      }


      try {

        return date.toLocaleTimeString(
          [],
          {
            hour: "numeric",
            minute: "2-digit",
          }
        );

      } catch {

        return "--";

      }

    };


  /* =========================================================
     EXPIRY
  ========================================================= */

  const getExpiryMillis =
    () => {

      if (
        !deliveryPerson?.availabilityExpiresAt
      ) {
        return null;
      }


      if (
        typeof
          deliveryPerson
            .availabilityExpiresAt
            .toMillis ===
        "function"
      ) {

        return deliveryPerson
          .availabilityExpiresAt
          .toMillis();

      }


      return new Date(
        deliveryPerson
          .availabilityExpiresAt
      ).getTime();

    };


  const expiryMillis =
    getExpiryMillis();


  const remainingMillis =
    expiryMillis
      ? Math.max(
          0,
          expiryMillis - now
        )
      : 0;


  const remainingMinutes =
    Math.floor(
      remainingMillis /
      60000
    );


  const remainingSeconds =
    Math.floor(
      (remainingMillis %
        60000) /
        1000
    );


  const remainingText =
    `${remainingMinutes
      .toString()
      .padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;


  /* =========================================================
     LOADING
  ========================================================= */

  if (
    authLoading ||
    loading
  ) {

    return (
      <div style={styles.loading}>

        <div style={styles.loadingIcon}>
          🚚
        </div>

        <h3>
          Loading delivery dashboard...
        </h3>

      </div>
    );

  }


  /* =========================================================
     NOT APPROVED
  ========================================================= */

  if (
    !deliveryPerson ||
    !(
      deliveryPerson.applicationStatus ===
        "APPROVED" ||
      deliveryPerson.verified ===
        true
    )
  ) {

    return (
      <div style={styles.page}>

        <div style={styles.card}>

          <div style={styles.bigIcon}>
            ⏳
          </div>

          <h2 style={styles.title}>
            Application Under Review
          </h2>

          <p style={styles.description}>
            Your delivery person account has
            not been approved yet.
          </p>

          <div style={styles.pendingBox}>

            <div style={styles.pendingLabel}>
              Application Status
            </div>

            <strong>
              {
                deliveryPerson?.applicationStatus ||
                "PENDING"
              }
            </strong>

          </div>

          <button
            style={styles.secondaryBtn}
            onClick={() =>
              navigate("/home")
            }
          >
            Back to Eyal
          </button>

        </div>

      </div>
    );

  }

  const filteredDeliveryAreas =
  ALLOWED_AREAS.filter((area) =>
    area
      .toLowerCase()
      .includes(areaSearch.toLowerCase())
  );


  /* =========================================================
     MAIN DASHBOARD
  ========================================================= */

  return (

    <div style={styles.page}>

      <div style={styles.container}>

        {/* HEADER */}

        <div style={styles.header}>

          <div>

            <div style={styles.headerSmall}>
              EYAL DELIVERY
            </div>

            <h2 style={styles.headerTitle}>
              Hello,{" "}
              {deliveryPerson.name ||
                "Delivery Partner"}
            </h2>

          </div>

          <div style={styles.headerIcon}>
            🚚
          </div>

        </div>


        {/* =================================================
            NEW DELIVERY REQUESTS
        ================================================= */}

        {deliveryRequests.length > 0 && (

          <div style={styles.section}>

            <div style={styles.sectionTitle}>
              🔔 New Delivery Requests
            </div>


            {deliveryRequests.map(
              (request) => (

                <div
                  key={request.id}
                  style={
                    styles.requestCard
                  }
                >

                  <div
                    style={
                      styles.requestTop
                    }
                  >

                    <div>

                      <div
                        style={
                          styles.requestBadge
                        }
                      >
                        NEW DELIVERY
                      </div>

                      <div
                        style={
                          styles.requestOrder
                        }
                      >
                        Order #
                        {request.orderId}
                      </div>

                    </div>

                    <div
                      style={
                        styles.requestTruck
                      }
                    >
                      📦
                    </div>

                  </div>


                  {/* =================================================
    PICKUP SHOP
================================================= */}

<div
  style={{
    marginTop: "14px",
    padding: "12px",
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: "12px",
  }}
>

  <div
    style={{
      fontSize: "13px",
      fontWeight: "800",
      color: "#c2410c",
      marginBottom: "7px",
    }}
  >
    🏪 PICKUP FROM
  </div>

  <strong
    style={{
      display: "block",
      fontSize: "15px",
      color: "#172033",
      marginBottom: "5px",
    }}
  >
    {request.shopName ||
      "Shop name unavailable"}
  </strong>

  {request.shopMobile && (
    <div
      style={{
        fontSize: "13px",
        color: "#4b5563",
        marginBottom: "3px",
      }}
    >
      📞 {request.shopMobile}
    </div>
  )}

  {request.shopAddress && (
    <div
      style={{
        fontSize: "13px",
        color: "#4b5563",
        marginBottom: "3px",
      }}
    >
      📍 {request.shopAddress}
    </div>
  )}

  {request.shopArea && (
    <div
      style={{
        fontSize: "13px",
        color: "#4b5563",
      }}
    >
      {request.shopArea}
    </div>
  )}

</div>


{/* =================================================
    CUSTOMER / DELIVERY ADDRESS
================================================= */}

<div
  style={{
    marginTop: "12px",
    padding: "12px",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "12px",
  }}
>

  <div
    style={{
      fontSize: "13px",
      fontWeight: "800",
      color: "#1d4ed8",
      marginBottom: "7px",
    }}
  >
    👤 DELIVER TO
  </div>

  <strong
    style={{
      display: "block",
      fontSize: "15px",
      color: "#172033",
      marginBottom: "5px",
    }}
  >
    {request.customerName}
  </strong>

  <div
    style={{
      fontSize: "13px",
      color: "#4b5563",
      marginBottom: "3px",
    }}
  >
    📞 {request.customerMobile}
  </div>

  <div
    style={{
      fontSize: "13px",
      color: "#4b5563",
      marginBottom: "3px",
    }}
  >
    📍 {request.address}
  </div>

  <div
    style={{
      fontSize: "13px",
      color: "#4b5563",
    }}
  >
    {request.area}
  </div>

</div>


                  <div
                    style={
                      styles.requestAmount
                    }
                  >

                    <span>
                      Payment
                    </span>

                    <strong>
                      ₹{request.total}
                    </strong>

                  </div>


                  <div
                    style={
                      styles.requestPayment
                    }
                  >
                    {request.paymentType ||
                      "Cash on Delivery"}
                  </div>


                  {request.status ===
                  "ASSIGNED" ? (

                    <div
                      style={
                        styles.requestActions
                      }
                    >

                      <button
                        disabled={saving}
                        onClick={() =>
                          acceptDelivery(
                            request
                          )
                        }
                        style={
                          styles.acceptBtn
                        }
                      >
                        ✓ ACCEPT
                      </button>



                    </div>

                  ) : (

                    <div
                      style={
                        styles.acceptedBadge
                      }
                    >
                      ✓ DELIVERY ACCEPTED
                    </div>

                  )}

                </div>

              )
            )}

          </div>

        )}


        {/* =================================================
    CURRENT DELIVERY
================================================= */}

<div style={styles.section}>

  <div style={styles.sectionTitle}>
    Current Delivery
  </div>


  {deliveryPerson.currentOrderId ? (

    <div
      style={
        styles.activeDeliveryCard
      }
    >

      <div
        style={
          styles.activeDeliveryIcon
        }
      >
        🚚
      </div>


      <div
        style={{
          flex: 1,
        }}
      >

        <div
          style={
            styles.activeDeliveryTitle
          }
        >
          Active Delivery
        </div>


        <div
          style={
            styles.activeDeliveryOrder
          }
        >
          Order #
          {
            deliveryPerson.currentOrderId
          }
        </div>


        <div
          style={
            styles.activeDeliveryStatus
          }
        >
          ✓ Accepted
        </div>


        {/* =========================================
            DELIVERED BUTTON
        ========================================= */}

        {deliveryRequests
          .filter(
            (request) =>
              request.orderId ===
                deliveryPerson.currentOrderId &&
              request.status ===
                "ACCEPTED"
          )
          .map(
            (request) => (

            <div
  style={{
    display: "flex",
    gap: "8px",
    marginTop: "12px",
    width: "100%",
    alignItems: "stretch",
  }}
>
  {/* CALL CUSTOMER */}
  <button
    type="button"
    disabled={saving}
    onClick={() => callCustomer(request)}
    style={{
      flex: 1,
      minWidth: 0,
      height: "48px",
      border: "none",
      borderRadius: "10px",
      padding: "0 8px",
      background: "#2563eb",
      color: "#fff",
      fontSize: "13px",
      fontWeight: "700",
      lineHeight: "16px",
      cursor: saving ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "5px",
      textAlign: "center",
      boxSizing: "border-box",
      opacity: saving ? 0.7 : 1,
    }}
  >
    <span style={{ fontSize: "17px" }}>📞</span>
    <span>CALL CUSTOMER</span>
  </button>

  {/* MARK AS DELIVERED */}
  <button
    type="button"
    disabled={saving}
    onClick={() => markDelivered(request)}
    style={{
      flex: 1,
      minWidth: 0,
      height: "48px",
      border: "none",
      borderRadius: "10px",
      padding: "0 8px",
      background: "#16a34a",
      color: "#fff",
      fontSize: "13px",
      fontWeight: "700",
      lineHeight: "16px",
      cursor: saving ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "5px",
      textAlign: "center",
      boxSizing: "border-box",
      opacity: saving ? 0.7 : 1,
    }}
  >
    <span style={{ fontSize: "17px" }}>✓</span>
    <span>
      {saving ? "UPDATING..." : "Mark as DELIVERED"}
    </span>
  </button>
</div>

            )
          )}

      </div>


      <span
        style={
          styles.activeBadge
        }
      >
        ACTIVE
      </span>

    </div>

  ) : (

    <div
      style={
        styles.emptyDelivery
      }
    >

      <div
        style={
          styles.emptyDeliveryIcon
        }
      >
        🛵
      </div>

      <div>

        <strong>
          No active delivery
        </strong>

        <p>
          Accepted delivery requests
          will appear here.
        </p>

      </div>

    </div>

  )}

</div>

{/* =========================================================
    DELIVERY AREAS & CHARGES
========================================================= */}

<div style={styles.deliveryChargeBox}>

  <div style={styles.deliveryChargeTitle}>
    💰 Delivery Areas & Charges
  </div>

  <div style={styles.deliveryAreasHint}>
    Choose the areas where you want to
    deliver and set a separate charge
    for each area.
  </div>

  {/* =====================================================
      EXISTING AREAS
  ===================================================== */}

  {deliveryAreas.length > 0 ? (

    <div style={styles.deliveryAreasList}>

      {deliveryAreas.map(
        (item, index) => (

          <div
            key={`${item.area}-${index}`}
            style={styles.deliveryAreaCard}
          >

            <div
              style={
                styles.deliveryAreaDetails
              }
            >

              <div
                style={
                  styles.deliveryAreaName
                }
              >
                📍 {item.area}
              </div>

              <div
                style={
                  styles.deliveryAreaCharge
                }
              >
                ₹{Number(item.charge || 0)}
              </div>

            </div>

            <button
              type="button"
              onClick={() =>
                removeDeliveryArea(index)
              }
              disabled={
                saving ||
                deliveryAreas.length <= 1
              }
              style={
                styles.removeAreaButton
              }
            >
              Remove
            </button>

          </div>

        )
      )}

    </div>

  ) : (

    <div
      style={
        styles.noDeliveryAreas
      }
    >
      No delivery areas added yet.
    </div>

  )}

  {/* =====================================================
      ADD NEW AREA
  ===================================================== */}

  <div
    style={
      styles.addDeliveryAreaBox
    }
  >

    <div
      style={
        styles.addDeliveryAreaTitle
      }
    >
      ➕ Add Delivery Area
    </div>

    {/* =====================================================
    CUSTOM DELIVERY AREA DROPDOWN
===================================================== */}

<div
  style={styles.areaDropdownContainer}
>

  {/* SELECTED AREA / TRIGGER */}

  <button
    type="button"
    onClick={() => {
      if (saving) return;

      setAreaDropdownOpen(
        !areaDropdownOpen
      );

      if (!areaDropdownOpen) {
        setAreaSearch("");
      }
    }}
    disabled={saving}
    style={styles.areaDropdownTrigger}
  >

    <span
      style={
        newDeliveryArea
          ? styles.areaSelectedText
          : styles.areaPlaceholder
      }
    >
      {newDeliveryArea ||
        "Select an area"}
    </span>

    <span
      style={
        styles.areaDropdownArrow
      }
    >
      {areaDropdownOpen
        ? "▲"
        : "▼"}
    </span>

  </button>


  {/* DROPDOWN */}

  {areaDropdownOpen && (

    <div
      style={
        styles.areaDropdownMenu
      }
    >

      {/* SEARCH */}

      <div
        style={
          styles.areaSearchBox
        }
      >

        <span
          style={
            styles.areaSearchIcon
          }
        >
          🔍
        </span>

        <input
          type="text"
          value={areaSearch}
          onChange={(e) =>
            setAreaSearch(
              e.target.value
            )
          }
          placeholder="Search area..."
          autoFocus
          style={
            styles.areaSearchInput
          }
        />

      </div>


      {/* AREA LIST */}

      <div
        style={
          styles.areaOptionsList
        }
      >

        {filteredDeliveryAreas.length >
        0 ? (

          filteredDeliveryAreas.map(
            (area) => (

              <button
                key={area}
                type="button"
                onClick={() => {

                  setNewDeliveryArea(
                    area
                  );

                  setAreaDropdownOpen(
                    false
                  );

                  setAreaSearch("");
                }}
                style={{
                  ...styles.areaOption,
                  ...(newDeliveryArea === area
                    ? styles.areaOptionSelected
                    : {}),
                }}
              >

                <span>
                  📍 {area}
                </span>

                {newDeliveryArea ===
                  area && (
                  <span>
                    ✓
                  </span>
                )}

              </button>

            )
          )

        ) : (

          <div
            style={
              styles.noAreaResults
            }
          >
            No matching area found.
          </div>

        )}

      </div>

    </div>

  )}

</div>

    <div
      style={
        styles.deliveryChargeRow
      }
    >

      <div
        style={
          styles.deliveryChargeInputWrap
        }
      >

        <span
          style={styles.rupeeSymbol}
        >
          ₹
        </span>

        <input
          type="number"
          min="0"
          max="10000"
          step="1"
          value={
            newDeliveryCharge
          }
          onChange={(e) =>
            setNewDeliveryCharge(
              e.target.value
            )
          }
          placeholder="Charge"
          style={
            styles.deliveryChargeInput
          }
          disabled={saving}
        />

      </div>

      <button
        type="button"
        onClick={() => {

          addDeliveryArea(
            newDeliveryArea,
            newDeliveryCharge
          );

          setNewDeliveryArea("");
          setNewDeliveryCharge("");

        }}
        disabled={
          saving ||
          !newDeliveryArea ||
          !newDeliveryCharge
        }
        style={
          styles.deliveryChargeButton
        }
      >
        {saving
          ? "Saving..."
          : "Add"}
      </button>

    </div>

  </div>

</div>


        {/* =================================================
            AVAILABILITY
        ================================================= */}

        <div
          style={{
            ...styles.availabilityCard,

            ...(deliveryPerson.available
              ? styles.availableCard
              : styles.offlineCard),
          }}
        >

          <div
            style={
              styles.availabilityTop
            }
          >

            <div>

              <div
                style={
                  styles.availabilityLabel
                }
              >
                DELIVERY AVAILABILITY
              </div>

              <div
                style={{
                  ...styles.availabilityStatus,

                  color:
  deliveryPerson.currentOrderId
    ? "#f59e0b"
    : deliveryPerson.available
      ? "#16a34a"
      : "#6b7280",
                }}
              >

                <span
                  style={{
                    ...styles.statusDot,

                    background:
  deliveryPerson.currentOrderId
    ? "#f59e0b"
    : deliveryPerson.available
      ? "#22c55e"
      : "#9ca3af",
                  }}
                />

                {deliveryPerson.currentOrderId
  ? "ON DELIVERY"
  : deliveryPerson.available
    ? "AVAILABLE"
    : "OFFLINE"}

              </div>

            </div>


            <div
              style={
                styles.statusEmoji
              }
            >
              {deliveryPerson.currentOrderId
  ? "🚚"
  : deliveryPerson.available
    ? "🟢"
    : "🔴"}
            </div>

          </div>


          {deliveryPerson.available ||
 deliveryPerson.currentOrderId ? (

            <>

              <div
                style={
                  styles.timerBox
                }
              >

                <div style={styles.timerLabel}>
  {deliveryPerson.currentOrderId
    ? "Delivery session remaining"
    : "Available for"}
</div>

                <div
                  style={
                    styles.timer
                  }
                >
                  {remainingText}
                </div>

                <div
                  style={
                    styles.expiryText
                  }
                >
                  Availability expires at{" "}
                  <strong>
                    {expiryMillis
                      ? formatTime(
                          new Date(
                            expiryMillis
                          )
                        )
                      : "--"}
                  </strong>
                </div>

              </div>


              {deliveryPerson.currentOrderId ? (

  <div
    style={{
      marginTop: 10,
      padding: 12,
      borderRadius: 10,
      background: "#fff7ed",
      color: "#c2410c",
      textAlign: "center",
      fontSize: 13,
      fontWeight: 700,
    }}
  >
    🚚 You are currently delivering this order.
  </div>

) : (

  <>
    <button
      disabled={saving}
      onClick={checkIn}
      style={styles.checkInBtn}
    >
      ✓ CHECK IN FOR 24 HOURS
    </button>

    <button
      disabled={saving}
      onClick={checkOut}
      style={styles.checkOutBtn}
    >
      Become Unavailable
    </button>
  </>

)}

            </>

          ) : (

            <>

              <p
                style={
                  styles.availabilityDescription
                }
              >
                Check in to become visible to
                customers looking for a delivery
                person.
              </p>

              <button
                disabled={saving}
                onClick={checkIn}
                style={
                  styles.checkInBtn
                }
              >
                🟢 CHECK IN
              </button>

            </>

          )}

        </div>


        {/* =================================================
            STATISTICS
        ================================================= */}

        <div style={styles.section}>

          <div style={styles.sectionTitle}>
            Your Performance
          </div>

          <div style={styles.statsGrid}>

            <div style={styles.statCard}>

              <div style={styles.statIcon}>
                📦
              </div>

              <div style={styles.statValue}>
                {
                  deliveryPerson
                    .totalDeliveries ??
                  0
                }
              </div>

              <div style={styles.statLabel}>
                Deliveries
              </div>

            </div>


            <div style={styles.statCard}>

              <div style={styles.statIcon}>
                ⭐
              </div>

              <div style={styles.statValue}>
                {
                  deliveryPerson.rating ??
                  0
                }
              </div>

              <div style={styles.statLabel}>
                Rating
              </div>

            </div>


            <div style={styles.statCard}>

              <div style={styles.statIcon}>
                🚚
              </div>

              <div style={styles.statValue}>
                {
                  deliveryPerson
                    .vehicleType ||
                  "Bike"
                }
              </div>

              <div style={styles.statLabel}>
                Vehicle
              </div>

            </div>

          </div>

        </div>

      </div>

    </div>

  );

}


/* =========================================================
   STYLES
========================================================= */

const styles = {

  page: {
    minHeight: "100dvh",
    background: "#f9fafb",
    padding: "16px",
    paddingTop: "70px",
    paddingBottom: "90px",
    boxSizing: "border-box",
  },

  container: {
    width: "100%",
    maxWidth: 600,
    margin: "0 auto",
  },

  loading: {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    background: "#f9fafb",
  },

  loadingIcon: {
    fontSize: 40,
    marginBottom: 10,
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 18,
  },

  headerSmall: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1,
  },

  headerTitle: {
    margin: "4px 0 0",
    fontSize: 23,
  },

  headerIcon: {
    width: 52,
    height: 52,
    borderRadius: 16,
    background: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 30,
  },

  section: {
    marginBottom: 18,
  },

  sectionTitle: {
    fontSize: 17,
    fontWeight: 700,
    marginBottom: 10,
  },

  /* REQUEST */

  requestCard: {
    background: "#fff",
    borderRadius: 18,
    padding: 16,
    marginBottom: 12,
    border: "1px solid #dbeafe",
    boxShadow:
      "0 5px 18px rgba(0,0,0,0.07)",
  },

  requestTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },

  requestBadge: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 800,
    color: "#2563eb",
    background: "#eff6ff",
    borderRadius: 999,
    padding: "5px 9px",
    marginBottom: 6,
  },

  requestOrder: {
    fontWeight: 800,
    fontSize: 16,
  },

  requestTruck: {
    fontSize: 32,
  },

  requestCustomer: {
    lineHeight: 1.7,
    fontSize: 14,
    color: "#475569",
    marginBottom: 12,
  },

  requestAmount: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 10,
    borderTop: "1px solid #e5e7eb",
    fontSize: 14,
  },

  requestPayment: {
    marginTop: 5,
    fontSize: 12,
    color: "#64748b",
  },

  requestActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginTop: 15,
  },

  acceptBtn: {
    height: 46,
    border: "none",
    borderRadius: 11,
    background: "#16a34a",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },

  rejectBtn: {
    height: 46,
    border: "none",
    borderRadius: 11,
    background: "#fee2e2",
    color: "#dc2626",
    fontWeight: 800,
    cursor: "pointer",
  },

  acceptedBadge: {
    marginTop: 14,
    padding: 12,
    borderRadius: 10,
    background: "#dcfce7",
    color: "#15803d",
    textAlign: "center",
    fontWeight: 700,
  },

  /* CURRENT DELIVERY */

  activeDeliveryCard: {
    background: "#fff",
    borderRadius: 16,
    padding: 15,
    display: "flex",
    alignItems: "center",
    gap: 12,
    boxShadow:
      "0 4px 15px rgba(0,0,0,0.06)",
    border:
      "1px solid #bbf7d0",
  },

  activeDeliveryIcon: {
    fontSize: 32,
  },

  activeDeliveryTitle: {
    fontWeight: 800,
  },

  activeDeliveryOrder: {
    fontSize: 13,
    color: "#475569",
    marginTop: 3,
  },

  activeDeliveryStatus: {
    fontSize: 12,
    color: "#16a34a",
    marginTop: 3,
    fontWeight: 700,
  },

  activeBadge: {
    background: "#dcfce7",
    color: "#15803d",
    borderRadius: 999,
    padding: "5px 8px",
    fontSize: 10,
    fontWeight: 800,
  },

  emptyDelivery: {
    background: "#fff",
    borderRadius: 16,
    padding: 18,
    display: "flex",
    alignItems: "center",
    gap: 14,
    boxShadow:
      "0 4px 15px rgba(0,0,0,0.05)",
  },

  emptyDeliveryIcon: {
    fontSize: 30,
  },

  /* AVAILABILITY */

  availabilityCard: {
    borderRadius: 18,
    padding: 18,
    marginBottom: 18,
  },

  availableCard: {
    background: "#f0fdf4",
    border:
      "1px solid #86efac",
  },

  offlineCard: {
    background: "#f8fafc",
    border:
      "1px solid #e2e8f0",
  },

  availabilityTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  availabilityLabel: {
    fontSize: 11,
    color: "#64748b",
    fontWeight: 700,
    letterSpacing: 0.5,
  },

  availabilityStatus: {
    marginTop: 5,
    fontSize: 21,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  statusDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
  },

  statusEmoji: {
    fontSize: 30,
  },

  timerBox: {
    background: "#fff",
    borderRadius: 15,
    padding: 15,
    textAlign: "center",
    marginTop: 14,
    marginBottom: 14,
  },

  timerLabel: {
    fontSize: 12,
    color: "#64748b",
  },

  timer: {
    fontSize: 39,
    fontWeight: 800,
    color: "#16a34a",
    letterSpacing: 2,
  },

  expiryText: {
    fontSize: 12,
    color: "#64748b",
  },

  checkInBtn: {
    width: "100%",
    height: 46,
    borderRadius: 11,
    border: "none",
    background: "#16a34a",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },

  checkOutBtn: {
    width: "100%",
    border: "none",
    background: "transparent",
    marginTop: 10,
    color: "#64748b",
    fontWeight: 600,
    cursor: "pointer",
  },

  availabilityDescription: {
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.5,
  },

  /* STATS */

  statsGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(3, 1fr)",
    gap: 10,
  },

  statCard: {
    background: "#fff",
    borderRadius: 14,
    padding: 14,
    textAlign: "center",
    boxShadow:
      "0 3px 12px rgba(0,0,0,0.05)",
  },

  statIcon: {
    fontSize: 22,
  },

  statValue: {
    fontSize: 20,
    fontWeight: 800,
    marginTop: 5,
  },

  statLabel: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 3,
  },

  /* APPROVAL */

  card: {
    width: "100%",
    maxWidth: 420,
    margin: "50px auto",
    background: "#fff",
    borderRadius: 18,
    padding: 22,
    boxSizing: "border-box",
    textAlign: "center",
    boxShadow:
      "0 5px 20px rgba(0,0,0,0.08)",
  },

  bigIcon: {
    fontSize: 45,
  },

  title: {
    marginBottom: 10,
  },

  description: {
    color: "#64748b",
    lineHeight: 1.5,
  },

  pendingBox: {
    background: "#f8fafc",
    borderRadius: 12,
    padding: 14,
    margin: "18px 0",
  },

  pendingLabel: {
    color: "#64748b",
    fontSize: 12,
    marginBottom: 5,
  },

  secondaryBtn: {
    width: "100%",
    height: 44,
    border: "none",
    borderRadius: 10,
    background: "#e2e8f0",
    color: "#334155",
    fontWeight: 700,
    cursor: "pointer",
  },

  deliveredBtn: {
  width: "100%",
  marginTop: 14,
  height: 44,
  borderRadius: 10,
  border: "none",
  background: "#16a34a",
  color: "#fff",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
},

deliveryChargeBox: {
  background: "#ffffff",
  borderRadius: 14,
  padding: 14,
  marginTop: 14,
  border: "1px solid #e5e7eb",
},

deliveryChargeTitle: {
  fontWeight: 700,
  fontSize: 15,
  marginBottom: 10,
},

deliveryChargeRow: {
  display: "flex",
  gap: 8,
  alignItems: "center",
  width: "100%",
},

deliveryChargeInputWrap: {
  flex: "1 1 0",
  minWidth: 0,
  height: 44,
  display: "flex",
  alignItems: "center",
  border: "1px solid #d1d5db",
  borderRadius: 10,
  background: "#fff",
  boxSizing: "border-box",
},

rupeeSymbol: {
  paddingLeft: 12,
  paddingRight: 4,
  fontWeight: 700,
  color: "#374151",
  flexShrink: 0,
},

deliveryChargeInput: {
  flex: "1 1 0",
  minWidth: 0,
  width: 0,
  height: "100%",
  border: "none",
  outline: "none",
  padding: "0 10px",
  fontSize: 16,
  background: "transparent",
  boxSizing: "border-box",
},

deliveryChargeButton: {
  flex: "0 0 90px",
  width: 90,
  height: 44,
  padding: "0 12px",
  border: "none",
  borderRadius: 10,
  background: "#16a34a",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
  boxSizing: "border-box",
},

deliveryChargeHint: {
  marginTop: 8,
  fontSize: 12,
  color: "#6b7280",
},

deliveryAreaInfo: {
  marginTop: 10,
  marginBottom: 14,
  padding: "10px 12px",
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e5e7eb",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
},

deliveryAreaLabel: {
  fontSize: 13,
  color: "#6b7280",
},

deliveryAreaValue: {
  fontSize: 14,
  color: "#111827",
  textAlign: "right",
},

/* =========================================================
   DELIVERY AREAS & CHARGES
========================================================= */

deliveryAreasHint: {
  fontSize: 12,
  color: "#64748b",
  lineHeight: 1.5,
  marginBottom: 12,
},

deliveryAreasList: {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  width: "100%",
},

deliveryAreaCard: {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  alignItems: "center",
  columnGap: 10,
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  padding: "12px",
  background: "#f8fafc",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
},

deliveryAreaDetails: {
  minWidth: 0,
  width: "100%",
},

deliveryAreaName: {
  display: "block",
  width: "100%",
  minWidth: 0,
  fontSize: 13,
  fontWeight: 600,
  color: "#374151",
  lineHeight: 1.45,

  /* Allow normal word wrapping */
  whiteSpace: "normal",
  wordBreak: "normal",
  overflowWrap: "break-word",
},

deliveryAreaCharge: {
  marginTop: 5,
  fontSize: 15,
  fontWeight: 700,
  color: "#16a34a",
  lineHeight: 1.3,
},

removeAreaButton: {
  width: "auto",
  minWidth: 68,
  maxWidth: 80,
  height: 34,
  flexShrink: 0,
  alignSelf: "center",

  padding: "0 10px",

  border: "1px solid #fecaca",
  borderRadius: 8,

  background: "#fef2f2",
  color: "#dc2626",

  fontSize: 11,
  fontWeight: 700,

  cursor: "pointer",
  whiteSpace: "nowrap",
},

noDeliveryAreas: {
  width: "100%",
  boxSizing: "border-box",
  padding: 14,
  textAlign: "center",

  background: "#f8fafc",
  border: "1px solid #e5e7eb",
  borderRadius: 10,

  color: "#64748b",
  fontSize: 12,
},

/* =========================================================
   ADD DELIVERY AREA
========================================================= */

addDeliveryAreaBox: {
  width: "100%",
  boxSizing: "border-box",

  marginTop: 14,
  padding: 12,

  background: "#ffffff",
  border: "1px dashed #cbd5e1",
  borderRadius: 12,
},

addDeliveryAreaTitle: {
  marginBottom: 9,

  fontSize: 13,
  fontWeight: 700,
  color: "#374151",
},

/* =========================================================
   CUSTOM AREA DROPDOWN
========================================================= */

areaDropdownContainer: {
  position: "relative",
  width: "100%",
  marginBottom: 10,
  boxSizing: "border-box",
},

areaDropdownTrigger: {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",

  width: "100%",
  minHeight: 44,

  boxSizing: "border-box",

  padding: "0 12px",

  border: "1px solid #d1d5db",
  borderRadius: 10,

  background: "#ffffff",
  color: "#374151",

  fontSize: 13,
  fontWeight: 500,

  textAlign: "left",
  cursor: "pointer",

  outline: "none",
},

areaSelectedText: {
  flex: 1,
  minWidth: 0,

  color: "#111827",
  fontWeight: 600,

  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
},

areaPlaceholder: {
  flex: 1,
  minWidth: 0,

  color: "#64748b",

  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
},

areaDropdownArrow: {
  flexShrink: 0,

  marginLeft: 8,

  fontSize: 10,
  color: "#64748b",
},

/* =========================================================
   DROPDOWN MENU
========================================================= */

areaDropdownMenu: {
  position: "absolute",

  top: "calc(100% + 5px)",
  left: 0,
  right: 0,

  zIndex: 9999,

  background: "#ffffff",

  border: "1px solid #d1d5db",
  borderRadius: 10,

  boxShadow:
    "0 8px 24px rgba(0, 0, 0, 0.15)",

  overflow: "hidden",
},

/* =========================================================
   SEARCH
========================================================= */

areaSearchBox: {
  display: "flex",
  alignItems: "center",

  height: 44,

  padding: "0 10px",

  borderBottom:
    "1px solid #e5e7eb",

  background: "#f8fafc",

  boxSizing: "border-box",
},

areaSearchIcon: {
  flexShrink: 0,

  marginRight: 7,

  fontSize: 13,
},

areaSearchInput: {
  flex: 1,
  minWidth: 0,

  height: "100%",

  border: "none",
  outline: "none",

  background: "transparent",

  color: "#111827",

  fontSize: 13,
},

/* =========================================================
   AREA OPTIONS
========================================================= */

areaOptionsList: {
  maxHeight: 240,

  overflowY: "auto",
  overflowX: "hidden",

  WebkitOverflowScrolling: "touch",
},

areaOption: {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",

  width: "100%",

  minHeight: 42,

  padding: "9px 12px",

  border: "none",
  borderBottom:
    "1px solid #f1f5f9",

  background: "#ffffff",

  color: "#374151",

  fontSize: 12,
  fontWeight: 500,

  textAlign: "left",

  cursor: "pointer",

  boxSizing: "border-box",
},

areaOptionSelected: {
  background: "#f0fdf4",
  color: "#15803d",
  fontWeight: 700,
},

noAreaResults: {
  padding: 18,

  textAlign: "center",

  color: "#64748b",

  fontSize: 12,
},

/* =========================================================
   CHARGE INPUT
========================================================= */

deliveryChargeRow: {
  display: "flex",
  alignItems: "center",
  width: "100%",
  gap: 8,
  boxSizing: "border-box",
},

deliveryChargeInputWrap: {
  display: "flex",
  alignItems: "center",

  flex: 1,
  minWidth: 0,
  height: 48,

  boxSizing: "border-box",

  border: "1px solid #d1d5db",
  borderRadius: 10,

  background: "#fff",
},

rupeeSymbol: {
  flexShrink: 0,

  paddingLeft: 12,

  fontSize: 16,
  fontWeight: 600,
  color: "#374151",
},

deliveryChargeInput: {
  flex: 1,
  minWidth: 0,
  width: "100%",
  height: "100%",

  boxSizing: "border-box",

  padding: "0 10px",

  border: "none",
  outline: "none",

  background: "transparent",
  color: "#111827",

  fontSize: 16,
},

deliveryChargeButton: {
  flexShrink: 0,

  width: "auto",
  minWidth: 82,
  height: 48,

  padding: "0 16px",

  border: "none",
  borderRadius: 10,

  background: "#16a34a",
  color: "#fff",

  fontSize: 13,
  fontWeight: 700,

  cursor: "pointer",
  whiteSpace: "nowrap",
},

deliveryChargeButtonDisabled: {
  background: "#94a3b8",
  color: "#ffffff",
  cursor: "not-allowed",
  opacity: 0.8,
},

};